import CustomSlider from 'components/Sliders/CustomSlider';
import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import 'features/Weather/assets/weatherDataDisplay.css';
import { getForecastDateLabel } from 'features/Weather/utils/forecastDate.utils';
import useLocation from 'hooks/useLocation';
import { useId, useState } from 'react';
import { getApiErrorMessage } from 'services/_infra/apiError.utils';
import { useGetCWActiveAlertsQuery } from 'services/alerts/alerts.api';
import { getBoundedValue } from 'utils/math/math.utils';
import ForecastAdvisoryList from '../../components/ForecastAdvisoryList/ForecastAdvisoryList';
import {
  DEFAULT_FORECAST_ADVISORY_DISTANCE_MILES,
  FORECAST_ADVISORIES_QUERY_OPTIONS,
  FORECAST_ADVISORIES_ROUTE,
  FORECAST_ADVISORY_MODES,
  MAXIMUM_FORECAST_ADVISORY_DISTANCE_MILES,
} from '../../config/forecastAdvisories.config';

const ForecastAdvisories = ({ localOnly = false, title }) => {
  const { locations } = useLocation();
  const headingId = useId();
  const distanceInputId = useId();
  const [maximumDistanceMiles, setMaximumDistanceMiles] = useState(
    DEFAULT_FORECAST_ADVISORY_DISTANCE_MILES,
  );
  const { data, error, isError, isLoading, refetch } = useGetCWActiveAlertsQuery(
    locations.weatherLocation?.coords,
    FORECAST_ADVISORIES_QUERY_OPTIONS,
  );
  const alerts = Array.isArray(data?.alerts) ? data.alerts : [];
  const hasUnavailableData = isError && !alerts.length;
  const mode = localOnly ? FORECAST_ADVISORY_MODES.local : FORECAST_ADVISORY_MODES.all;
  const pageHeading = getForecastDateLabel(new Date(), title) ?? title;

  const updateMaximumDistance = (value) => {
    setMaximumDistanceMiles(
      getBoundedValue(Number(value) || 0, 0, MAXIMUM_FORECAST_ADVISORY_DISTANCE_MILES),
    );
  };

  return (
    <section
      aria-labelledby={headingId}
      className="weather-data-display d-flex h-100 min-h-0 w-100 flex-column gap-3 overflow-hidden"
    >
      <WidgetTimeUpdate route={FORECAST_ADVISORIES_ROUTE} realtime />

      {isError && !hasUnavailableData && (
        <div className="alert alert-warning m-0 py-2" role="alert">
          The latest update failed. Displaying the most recently available advisories.
        </div>
      )}

      <header className="flex-shrink-0">
        <div className="weather-data-display-heading border-bottom pb-2">
          <h2 className="weather-data-display-title m-0 fw-bold lh-sm" id={headingId}>
            {pageHeading}
          </h2>
        </div>

        {localOnly && (
          <div className="d-flex flex-column gap-2 px-3 pt-3 flex-sm-row align-items-sm-center gap-sm-3">
            <label className="mb-0 flex-shrink-0 fw-semibold" htmlFor={distanceInputId}>
              Maximum distance (miles)
            </label>
            <div className="d-flex w-100 align-items-center gap-3">
              <input
                className="form-control form-control-sm w-auto flex-shrink-0"
                id={distanceInputId}
                max={MAXIMUM_FORECAST_ADVISORY_DISTANCE_MILES}
                min={0}
                onChange={(event) => updateMaximumDistance(event.target.value)}
                type="number"
                value={maximumDistanceMiles}
              />
              <div className="flex-grow-1" style={{ minWidth: 120, maxWidth: 320 }}>
                <CustomSlider
                  max={MAXIMUM_FORECAST_ADVISORY_DISTANCE_MILES}
                  min={0}
                  onChange={updateMaximumDistance}
                  step={5}
                  title="Maximum advisory distance in miles"
                  value={maximumDistanceMiles}
                />
              </div>
            </div>
          </div>
        )}
      </header>

      {isLoading ? (
        <div className="d-flex min-h-0 flex-grow-1 align-items-center justify-content-center">
          <WidgetLoadingAnimation widgetName="ForecastAdvisories" />
        </div>
      ) : hasUnavailableData ? (
        <ErrorMessage message={getApiErrorMessage(error)} onRetry={refetch} />
      ) : (
        <div className="min-h-0 flex-grow-1 overflow-auto">
          <ForecastAdvisoryList
            alerts={alerts}
            maximumDistanceMiles={maximumDistanceMiles}
            mode={mode}
          />
        </div>
      )}
    </section>
  );
};

export default ForecastAdvisories;
